import { sdk } from "@/lib/medusa";
import { ORDER_TYPE } from "@/lib/orderType";

function queryString(params = {}) {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });

  const value = search.toString();
  return value ? `?${value}` : "";
}

// export function getOrderType(order) {
//   const value = String(
//     order?.metadata?.order_type ||
//       order?.metadata?.asian_basket_order_type ||
//       order?.asian_basket_order_type ||
//       "",
//   )
//     .trim()
//     .toLowerCase();

//   if (value === ORDER_TYPE.TAKEOUT || value === "prepared food") {
//     return ORDER_TYPE.TAKEOUT;
//   }

//   return ORDER_TYPE.GROCERY;
// }

export function getOrderType(order) {
  /* =======================================================
     1. EXPLICIT ORDER TYPE

     Use this first if later we persist order_type directly
     on the Medusa order.
     ======================================================= */

  const explicitType = String(
    order?.metadata?.order_type ||
      order?.metadata?.asian_basket_order_type ||
      order?.asian_basket_order_type ||
      "",
  )
    .trim()
    .toLowerCase();

  if (
    explicitType === ORDER_TYPE.TAKEOUT ||
    explicitType === "prepared food" ||
    explicitType === "take-out"
  ) {
    return ORDER_TYPE.TAKEOUT;
  }

  if (explicitType === ORDER_TYPE.GROCERY || explicitType === "grocery") {
    return ORDER_TYPE.GROCERY;
  }

  /* =======================================================
     2. FALLBACK TO ORDER ITEMS

     Medusa stores the original product type on each
     order line item.

     Grocery product:
       product_type = "Grocery"

     Takeout product:
       product_type = "Prepared Food"
     ======================================================= */

  const itemTypes = (order?.items || [])
    .map((item) =>
      String(
        item?.product_type ||
          item?.variant?.product?.type?.value ||
          item?.variant?.product?.type ||
          "",
      )
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean);

  const hasTakeoutProduct = itemTypes.some(
    (type) =>
      type === "prepared food" || type === "takeout" || type === "take-out",
  );

  if (hasTakeoutProduct) {
    return ORDER_TYPE.TAKEOUT;
  }

  const hasGroceryProduct = itemTypes.some((type) => type === "grocery");

  if (hasGroceryProduct) {
    return ORDER_TYPE.GROCERY;
  }

  /* =======================================================
     3. LAST FALLBACK

     Only for old/unknown orders where product type
     information doesn't exist.
     ======================================================= */

  return ORDER_TYPE.GROCERY;
}

export async function listCustomerOrders({ limit = 50, offset = 0 } = {}) {
  const response = await sdk.client.fetch(
    `/store/orders${queryString({ limit, offset })}`,
    { method: "GET" },
  );

  return {
    orders: response?.orders || [],
    count: Number(response?.count || response?.orders?.length || 0),
    limit: Number(response?.limit || limit),
    offset: Number(response?.offset || offset),
  };
}

export async function retrieveCustomerOrder(orderId) {
  if (!orderId) throw new Error("Order ID is required.");

  const response = await sdk.client.fetch(`/store/orders/${orderId}`, {
    method: "GET",
  });

  return response?.order || null;
}
export async function listCustomerOrdersDetailed(options = {}) {
  const result = await listCustomerOrders(options);

  const orders = await Promise.all(
    result.orders.map(async (order) => {
      try {
        const detail = await retrieveCustomerOrder(order.id);
        return detail || order;
      } catch {
        return order;
      }
    }),
  );

  return {
    ...result,
    orders,
  };
}

export async function updateCustomerProfile(data) {
  const response = await sdk.client.fetch("/store/customers/me", {
    method: "POST",
    body: data,
  });

  return response?.customer || null;
}

export async function listCustomerAddresses() {
  const response = await sdk.client.fetch(
    "/store/customers/me/addresses?limit=100&offset=0",
    { method: "GET" },
  );

  return response?.addresses || [];
}

export async function createCustomerAddress(data) {
  const response = await sdk.client.fetch("/store/customers/me/addresses", {
    method: "POST",
    body: data,
  });

  return response?.customer || null;
}

export async function updateCustomerAddress(addressId, data) {
  if (!addressId) throw new Error("Address ID is required.");

  const response = await sdk.client.fetch(
    `/store/customers/me/addresses/${addressId}`,
    {
      method: "POST",
      body: data,
    },
  );

  return response?.customer || null;
}

export async function deleteCustomerAddress(addressId) {
  if (!addressId) throw new Error("Address ID is required.");

  return sdk.client.fetch(`/store/customers/me/addresses/${addressId}`, {
    method: "DELETE",
  });
}

export function flattenBuyAgainItems(orders = []) {
  const byVariant = new Map();

  [...orders]
    .sort(
      (a, b) =>
        new Date(b?.created_at || 0).getTime() -
        new Date(a?.created_at || 0).getTime(),
    )
    .forEach((order) => {
      const orderType = getOrderType(order);

      (order?.items || []).forEach((item) => {
        const variantId = item?.variant_id || item?.variant?.id;
        if (!variantId || byVariant.has(variantId)) return;

        byVariant.set(variantId, {
          ...item,
          variant_id: variantId,
          asian_basket_order_type: orderType,
          last_ordered_at: order?.created_at,
          source_order_id: order?.id,
        });
      });
    });

  return [...byVariant.values()];
}

export async function changeCustomerPassword({ currentPassword, newPassword }) {
  const response = await sdk.client.fetch(
    "/store/customers/me/change-password",
    {
      method: "POST",

      body: {
        current_password: currentPassword,

        new_password: newPassword,
      },
    },
  );

  return response;
}
